const PRODUCT_ORDER_ITEM_CANDIDATE_FIELDS = ["lineId", "orderItemId", "OrderItemId"];
const PRODUCT_LEGACY_CANDIDATE_FIELDS = ["dbItemId", "DBItemId", "productId", "ProductId", "id", "Id"];
const SECTION_ID_CANDIDATE_FIELDS = ["sectionId", "id", "Id"];

const normalizeText = (value) => String(value ?? "").trim();

const normalizeIdentity = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = normalizeText(value);
  return normalized ? normalized : null;
};

const collectIdentityCandidates = (value = {}, fields = []) => {
  const candidates = [];
  fields.forEach((field) => {
    const normalized = normalizeIdentity(value?.[field]);
    if (normalized && !candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  });
  return candidates;
};

const buildSectionTitle = (section = {}, row = {}) => normalizeText(section?.title ?? row?.title ?? "");
const buildSectionStyle = (section = {}, row = {}) =>
  String(section?.style ?? section?.Style ?? row?.style ?? row?.Style ?? "").trim().toLowerCase() === "dark"
    ? "dark"
    : "light";

export const resolveCustomerDocumentLayoutRows = ({
  products = [],
  cartSections = [],
  layout = [],
} = {}) => {
  const normalizedProducts = Array.isArray(products) ? products.filter(Boolean) : [];
  const normalizedSections = Array.isArray(cartSections) ? cartSections.filter(Boolean) : [];
  const normalizedLayout = Array.isArray(layout) ? layout.filter(Boolean) : [];

  if (!normalizedLayout.length) {
    return normalizedProducts.map((product, index) => ({
      type: "product",
      key: `product:${normalizeIdentity(product?.lineId) || normalizeIdentity(product?.orderItemId) || normalizeIdentity(product?.OrderItemId) || normalizeIdentity(product?.id) || normalizeIdentity(product?.Id) || index}`,
      product,
    }));
  }

  const productEntries = normalizedProducts.map((product, index) => ({
    product,
    index,
    matched: false,
    orderItemCandidates: collectIdentityCandidates(product, PRODUCT_ORDER_ITEM_CANDIDATE_FIELDS),
    legacyCandidates: collectIdentityCandidates(product, PRODUCT_LEGACY_CANDIDATE_FIELDS),
  }));

  const productLookup = new Map();
  const legacyProductLookup = new Map();
  productEntries.forEach((entry) => {
    entry.orderItemCandidates.forEach((candidate) => {
      if (!productLookup.has(candidate)) productLookup.set(candidate, []);
      productLookup.get(candidate).push(entry);
    });
    entry.legacyCandidates.forEach((candidate) => {
      if (!legacyProductLookup.has(candidate)) legacyProductLookup.set(candidate, []);
      legacyProductLookup.get(candidate).push(entry);
    });
  });

  const sectionLookup = new Map();
  normalizedSections.forEach((section, index) => {
    const candidates = collectIdentityCandidates(section, SECTION_ID_CANDIDATE_FIELDS);
    candidates.forEach((candidate) => {
      if (!sectionLookup.has(candidate)) {
        sectionLookup.set(candidate, section);
      }
    });
    if (!candidates.length) {
      sectionLookup.set(`__index:${index}`, section);
    }
  });

  const rows = [];

  normalizedLayout.forEach((row, index) => {
    const type = normalizeText(row?.type).toLowerCase();

    if (type === "section") {
      const sectionCandidates = collectIdentityCandidates(row, SECTION_ID_CANDIDATE_FIELDS);
      const section =
        sectionCandidates.map((candidate) => sectionLookup.get(candidate)).find(Boolean) ||
        sectionLookup.get(`__index:${index}`) ||
        row;
      const title = buildSectionTitle(section, row);
      if (!title) return;
      rows.push({
        type: "section",
        key: `section:${sectionCandidates[0] || normalizeIdentity(section?.sectionId) || normalizeIdentity(section?.id) || index}`,
        title,
        style: buildSectionStyle(section, row),
        section,
      });
      return;
    }

    if (type === "product") {
      const productCandidates = collectIdentityCandidates(row, PRODUCT_ORDER_ITEM_CANDIDATE_FIELDS);
      let entry = productCandidates
        .flatMap((candidate) => productLookup.get(candidate) || [])
        .find((candidateEntry) => !candidateEntry.matched);

      if (!entry) {
        const legacyCandidates = collectIdentityCandidates(row, PRODUCT_LEGACY_CANDIDATE_FIELDS);
        entry = legacyCandidates
          .map((candidate) => legacyProductLookup.get(candidate) || [])
          .find((matches) => matches.length === 1 && !matches[0].matched)?.[0];
      }

      if (!entry) return;

      entry.matched = true;
      rows.push({
        type: "product",
        key: `product:${productCandidates[0] || entry.orderItemCandidates[0] || entry.legacyCandidates[0] || entry.index}`,
        product: entry.product,
      });
    }
  });

  productEntries
    .filter((entry) => !entry.matched)
    .forEach((entry) => {
      rows.push({
        type: "product",
        key: `product:${entry.orderItemCandidates[0] || entry.legacyCandidates[0] || entry.index}`,
        product: entry.product,
      });
    });

  return rows;
};

export const customerDocumentLayoutTestUtils = {
  normalizeIdentity,
  normalizeText,
  collectIdentityCandidates,
};
